import React, { useEffect, useState } from "react";
import { Dialog, Tabs } from "components/ui";
import { Loading } from "components/shared";
import supabaseClient from "utils/supabaseClient";
import axios from "axios";
import slugify from "slugify";
import { BiLinkExternal } from "react-icons/bi";

const { TabNav, TabList, TabContent } = Tabs;

const normalizeCta = (cta) => {
  if (!cta) return null;
  if (typeof cta === "string") return null;
  if (Array.isArray(cta)) return cta.length > 0 ? cta[0] : null;
  if (typeof cta === "object" && !cta.title && !cta.id) return null;
  return cta;
};

const getEventScope = (item) => {
  const eventName = item?.event_name || "";
  if (eventName.startsWith("cta-global-")) return "global";
  if (eventName.startsWith("cta-category-")) return "category";

  const meta = item?.metadata;
  if (!meta) return null;

  try {
    const parsed = typeof meta === "string" ? JSON.parse(meta) : meta;
    return parsed?.cta_scope === "global" || parsed?.cta_scope === "category"
      ? parsed.cta_scope
      : null;
  } catch {
    return null;
  }
};

const getTitleSlug = (title) => slugify(title || "", { lower: true, strict: true });

const legacyEventMatchesCta = (item, cta) => {
  if (!cta?.title) return false;

  const titleSlug = getTitleSlug(cta.title);
  const eventName = item?.event_name || "";

  if (item?.name === cta.title) return true;
  if (!titleSlug) return false;

  return (
    eventName.startsWith(`cta-global-${titleSlug}-`) ||
    eventName.startsWith(`cta-category-${titleSlug}-`) ||
    eventName.startsWith(`cta-${titleSlug}-`)
  );
};

const eventMatchesScope = (item, scope, context = {}) => {
  const { globalCta, categoryCta, activeScope } = context;
  const eventScope = getEventScope(item);
  if (eventScope) return eventScope === scope;

  const eventName = item?.event_name || "";
  if (!eventName.startsWith("cta-")) return false;

  const targetCta = scope === "global" ? globalCta : categoryCta;
  const otherCta = scope === "global" ? categoryCta : globalCta;

  if (legacyEventMatchesCta(item, targetCta)) {
    if (legacyEventMatchesCta(item, otherCta) && activeScope) {
      return activeScope === scope;
    }
    return true;
  }

  // Legacy CTA events can remain after CTA title changes.
  // If we cannot map by title, fall back to the currently active CTA scope.
  if (activeScope && !eventName.startsWith("cta-category-")) {
    return activeScope === scope;
  }

  if (!targetCta?.title && activeScope === scope) {
    return true;
  }

  if (!targetCta?.title && !activeScope) {
    if (scope === "global") return !eventName.startsWith("cta-category-");
    if (scope === "category") return eventName.startsWith("cta-category-");
  }

  return false;
};

const PostAnalyticsDialog = ({
  isOpen,
  onClose,
  postSlug,
  postPage,
  authorId,
  blogDomain,
  initialTab = "pageviews",
  onOpenAnalyticsPage,
}) => {
  const [loading, setLoading] = useState(false);
  const [clicksData, setClicksData] = useState([]);
  const [pageViewData, setPageViewData] = useState(null);
  const [ctrMeta, setCtrMeta] = useState(null);
  const [newsletterData, setNewsletterData] = useState({ 
    subscribers: [], 
    stats: { currentCount: 0, previousCount: 0, subscriptionRate: 0 } 
  });
  const [activeTab, setActiveTab] = useState(initialTab);
  const [period, setPeriod] = useState("7d");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [applyCustom, setApplyCustom] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);


  useEffect(() => {
    if (isOpen && postSlug && authorId && activeTab === "clicks") {
      fetchClicksAnalytics();
    }
    if (isOpen && postSlug && authorId && activeTab === "pageviews") {
      fetchPageViews();
    }
    if (isOpen && postSlug && authorId && activeTab === "newsletter") {
      fetchNewsletterSubscribers();
    }
  }, [isOpen, postSlug, postPage, authorId, activeTab, period, applyCustom]);

  const getPeriodRange = () => {
    let startDate = new Date();
    let endDate = new Date();

    if (period === "7d") {
      startDate.setDate(startDate.getDate() - 7);
    } else if (period === "30d") {
      startDate.setDate(startDate.getDate() - 30);
    } else if (period === "6mo") {
      startDate.setMonth(startDate.getMonth() - 6);
    } else if (period === "custom") {
      if (!fromDate || !toDate) {
        return { error: true };
      }
      startDate = new Date(fromDate);
      endDate = new Date(toDate);
      endDate.setHours(23, 59, 59, 999);
    }

    let periodDays;
    if (period === "custom") {
      periodDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    } else {
      periodDays = period === "7d" ? 7 : period === "30d" ? 30 : period === "6mo" ? 180 : 7;
    }

    const prevStartDate = new Date(startDate);
    prevStartDate.setDate(prevStartDate.getDate() - periodDays);

    return { startDate, endDate, periodDays, prevStartDate, error: false };
  };

  const fetchPostCtaContext = async () => {
    const [postRes, authorRes] = await Promise.all([
      supabaseClient
        .from("posts")
        .select("category!inner(id, cta!cta(*))")
        .eq("posttitle", postSlug)
        .eq("postedby", authorId)
        .maybeSingle(),
      supabaseClient
        .from("authors")
        .select("cta!authors_cta_fkey(*)")
        .eq("id", authorId)
        .maybeSingle(),
    ]);

    if (postRes.error) {
      console.error("[Post CTA context] post query:", postRes.error);
    }
    if (authorRes.error) {
      console.error("[Post CTA context] author query:", authorRes.error);
    }

    let categoryCta = normalizeCta(postRes.data?.category?.cta);
    let globalCta = normalizeCta(authorRes.data?.cta);

    if (!globalCta) {
      const { data: authorRow } = await supabaseClient
        .from("authors")
        .select("cta")
        .eq("id", authorId)
        .maybeSingle();

      if (authorRow?.cta) {
        const { data: ctaRow } = await supabaseClient
          .from("cta")
          .select("*")
          .eq("id", authorRow.cta)
          .maybeSingle();
        globalCta = normalizeCta(ctaRow);
      }
    }

    if (!globalCta) {
      const { data: globalCtaRow } = await supabaseClient
        .from("cta")
        .select("*")
        .eq("created_by", authorId)
        .is("type", null)
        .limit(1)
        .maybeSingle();
      globalCta = normalizeCta(globalCtaRow);
    }

    if (!categoryCta && postRes.data?.category?.id) {
      const { data: catRow } = await supabaseClient
        .from("category")
        .select("cta!cta(*)")
        .eq("id", postRes.data.category.id)
        .maybeSingle();
      categoryCta = normalizeCta(catRow?.cta);
    }

    const activeScope = categoryCta ? "category" : globalCta ? "global" : null;
    const activeCtaTitle = categoryCta?.title || globalCta?.title || null;

    return { categoryCta, globalCta, activeScope, activeCtaTitle };
  };

  const fetchClicksAnalytics = async () => {
    setLoading(true);
    try {
      let startDate = new Date();
      let endDate = new Date();
      
      if (period === "7d") {
        startDate.setDate(startDate.getDate() - 7);
      } else if (period === "30d") {
        startDate.setDate(startDate.getDate() - 30);
      } else if (period === "6mo") {
        startDate.setMonth(startDate.getMonth() - 6);
      } else if (period === "custom") {
        if (!fromDate || !toDate) {
          console.error("Custom period requires from and to dates");
          setClicksData([]);
          setLoading(false);
          return;
        }
        startDate = new Date(fromDate);
        endDate = new Date(toDate);
        endDate.setHours(23, 59, 59, 999);
      }

      // Fetch CTA clicks
      const ctaQuery = supabaseClient
        .from("analytics_events")
        .select("name, created_at, event_name, metadata")
        .eq("source", postSlug)
        .eq("author", authorId)
        .eq("type", "click")
        .like("event_name", "cta-%")
        .gte("created_at", startDate.toISOString());

      if (period === "custom") {
        ctaQuery = ctaQuery.lte("created_at", endDate.toISOString());
      }

      const { data: rawCtaData, error: ctaError } = await ctaQuery;

      if (ctaError) throw ctaError;

      // Combine all CTA events (including global/category) into a single CTA view
      const ctaData = rawCtaData || [];

      // Fetch Button clicks
      const buttonQuery = supabaseClient
        .from("analytics_events")
        .select("name, created_at")
        .eq("source", postSlug)
        .eq("author", authorId)
        .eq("type", "click")
        .like("event_name", "button-%")
        .gte("created_at", startDate.toISOString());

      if (period === "custom") {
        buttonQuery = buttonQuery.lte("created_at", endDate.toISOString());
      }

      const { data: buttonData, error: buttonError } = await buttonQuery;

      if (buttonError) throw buttonError;

      // Calculate period days for previous period comparison
      let periodDays;
      if (period === "custom") {
        periodDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      } else {
        periodDays = period === "7d" ? 7 : period === "30d" ? 30 : period === "6mo" ? 180 : 7;
      }
      const prevStartDate = new Date(startDate);
      prevStartDate.setDate(prevStartDate.getDate() - periodDays);

      // Fetch previous period CTA clicks
      const { data: prevCtaData } = await supabaseClient
        .from("analytics_events")
        .select("name, created_at, event_name, metadata")
        .eq("source", postSlug)
        .eq("author", authorId)
        .eq("type", "click")
        .like("event_name", "cta-%")
        .gte("created_at", prevStartDate.toISOString())
        .lt("created_at", startDate.toISOString());

      // Fetch previous period Button clicks
      const { data: prevButtonData } = await supabaseClient
        .from("analytics_events")
        .select("name, created_at")
        .eq("source", postSlug)
        .eq("author", authorId)
        .eq("type", "click")
        .like("event_name", "button-%")
        .gte("created_at", prevStartDate.toISOString())
        .lt("created_at", startDate.toISOString());

      // Process CTA data
      const ctaGrouped = {};
      ctaData?.forEach((item) => {
        const name = item.name || "Unknown CTA";
        if (!ctaGrouped[name]) {
          ctaGrouped[name] = { name, count: 0, lastClicked: null, prevCount: 0, type: 'cta' };
        }
        ctaGrouped[name].count++;
        if (!ctaGrouped[name].lastClicked || new Date(item.created_at) > new Date(ctaGrouped[name].lastClicked)) {
          ctaGrouped[name].lastClicked = item.created_at;
        }
      });

      const prevCtaGrouped = {};
      prevCtaData?.forEach((item) => {
        const name = item.name || "Unknown CTA";
        if (!prevCtaGrouped[name]) {
          prevCtaGrouped[name] = 0;
        }
        prevCtaGrouped[name]++;
      });

      // Process Button data
      const buttonGrouped = {};
      buttonData?.forEach((item) => {
        const name = item.name || "Unknown Button";
        if (!buttonGrouped[name]) {
          buttonGrouped[name] = { name, count: 0, lastClicked: null, prevCount: 0, type: 'button' };
        }
        buttonGrouped[name].count++;
        if (!buttonGrouped[name].lastClicked || new Date(item.created_at) > new Date(buttonGrouped[name].lastClicked)) {
          buttonGrouped[name].lastClicked = item.created_at;
        }
      });

      const prevButtonGrouped = {};
      prevButtonData?.forEach((item) => {
        const name = item.name || "Unknown Button";
        if (!prevButtonGrouped[name]) {
          prevButtonGrouped[name] = 0;
        }
        prevButtonGrouped[name]++;
      });

      // Calculate trends for CTA
      Object.keys(ctaGrouped).forEach((name) => {
        ctaGrouped[name].prevCount = prevCtaGrouped[name] || 0;
        
        if (ctaGrouped[name].prevCount > 0) {
          const change = ((ctaGrouped[name].count - ctaGrouped[name].prevCount) / ctaGrouped[name].prevCount) * 100;
          ctaGrouped[name].trend = Math.round(change);
        } else if (ctaGrouped[name].count > 0) {
          ctaGrouped[name].trend = 100;
        } else {
          ctaGrouped[name].trend = 0;
        }
      });

      // Calculate trends for Buttons
      Object.keys(buttonGrouped).forEach((name) => {
        buttonGrouped[name].prevCount = prevButtonGrouped[name] || 0;
        
        if (buttonGrouped[name].prevCount > 0) {
          const change = ((buttonGrouped[name].count - buttonGrouped[name].prevCount) / buttonGrouped[name].prevCount) * 100;
          buttonGrouped[name].trend = Math.round(change);
        } else if (buttonGrouped[name].count > 0) {
          buttonGrouped[name].trend = 100;
        } else {
          buttonGrouped[name].trend = 0;
        }
      });

      // Combine and sort all data
      const allClicks = [...Object.values(ctaGrouped), ...Object.values(buttonGrouped)];
      const sortedData = allClicks.sort((a, b) => b.count - a.count);
      setClicksData(sortedData);
    } catch (err) {
      console.error("[Clicks Analytics] Error:", err);
      setClicksData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPageViews = async () => {
    setLoading(true);
    try {
      if (period === "custom" && (!fromDate || !toDate)) {
        console.error("Custom period requires from and to dates");
        setPageViewData(null);
        setLoading(false);
        return;
      }

      const params = {
        postSlug,
        authorId,
        period,
        domain: blogDomain,
        ...(postPage && { pagePath: postPage }),
        ...(period === "custom" && {
          from: fromDate,
          to: toDate,
        }),
      };

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/plausible/post-analytics`,
        { params }
      );

      const data = res.data?.data;
      const meta = res.data?.meta;
      
      if (data && data.current && data.previous) {
        // Calculate trends
        const calculateTrend = (current, previous) => {
          if (previous === 0) return current > 0 ? 100 : 0;
          return Math.round(((current - previous) / previous) * 100);
        };

        // Inverse logic for bounce rate (lower is better)
        const calculateBounceTrend = (current, previous) => {
          if (previous === 0) return current > 0 ? -100 : 0;
          return Math.round(((previous - current) / previous) * 100);
        };

        const pageViewsWithTrends = {
          pageviews: data.current.pageviews,
          pageviewsTrend: calculateTrend(data.current.pageviews, data.previous.pageviews),
          
          visitors: data.current.visitors,
          visitorsTrend: calculateTrend(data.current.visitors, data.previous.visitors),
          
          bounce_rate: data.current.bounce_rate,
          bounceRateTrend: calculateBounceTrend(data.current.bounce_rate, data.previous.bounce_rate),
          
          visit_duration: data.current.visit_duration,
          visitDurationTrend: calculateTrend(data.current.visit_duration, data.previous.visit_duration),
          
          cta_clicks: data.current.cta_clicks,
          ctaClicksTrend: calculateTrend(data.current.cta_clicks, data.previous.cta_clicks),
          
          button_clicks: data.current.button_clicks,
          buttonClicksTrend: calculateTrend(data.current.button_clicks, data.previous.button_clicks),
          
          ctr: data.current.ctr,
          ctrTrend: calculateTrend(parseFloat(data.current.ctr), parseFloat(data.previous.ctr)),
          
          button_ctr: data.current.button_ctr,
          buttonCtrTrend: calculateTrend(parseFloat(data.current.button_ctr), parseFloat(data.previous.button_ctr)),
        };

        setPageViewData(pageViewsWithTrends);
        setCtrMeta(meta?.ctrCalculatedFrom || null);
      } else {
        setPageViewData(null);
        setCtrMeta(null);
      }
    } catch (err) {
      console.error("[Page Views] Error:", err);
      setPageViewData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsletterSubscribers = async () => {
    setLoading(true);
    try {
      const range = getPeriodRange();
      if (range.error) {
        setNewsletterData({
          subscribers: [],
          stats: { currentCount: 0, previousCount: 0, subscriptionRate: 0 },
        });
        setLoading(false);
        return;
      }

      const { startDate, endDate, prevStartDate } = range;

      // Query current period subscribers
      let query = supabaseClient
        .from("newsletter")
        .select("email, name, created_at, url")
        .eq("author", authorId)
        .like("url", `%/posts/${postSlug}%`)
        .gte("created_at", startDate.toISOString());

      if (period === "custom") {
        query = query.lte("created_at", endDate.toISOString());
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;

      // Query previous period subscribers
      const { data: prevData } = await supabaseClient
        .from("newsletter")
        .select("email, created_at")
        .eq("author", authorId)
        .like("url", `%/posts/${postSlug}%`)
        .gte("created_at", prevStartDate.toISOString())
        .lt("created_at", startDate.toISOString());

      const currentCount = data?.length || 0;
      const previousCount = prevData?.length || 0;

      // Calculate subscription rate (trend)
      let subscriptionRate = 0;
      if (previousCount > 0) {
        subscriptionRate = Math.round(((currentCount - previousCount) / previousCount) * 100);
      } else if (currentCount > 0) {
        subscriptionRate = 100; // New subscribers, no previous data
      }

      const subscribers = data?.map((item) => ({
        email: item.email || "Unknown",
        name: item.name || null,
        subscribedAt: item.created_at,
        url: item.url
      })) || [];

      // Set data with stats
      setNewsletterData({
        subscribers,
        stats: {
          currentCount,
          previousCount,
          subscriptionRate
        }
      });
    } catch (err) {
      console.error("[Newsletter] Error:", err);
      setNewsletterData({ subscribers: [], stats: { currentCount: 0, previousCount: 0, subscriptionRate: 0 } });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === "clicks") {
      fetchClicksAnalytics();
    } else if (activeTab === "pageviews") {
      fetchPageViews();
    } else if (activeTab === "newsletter") {
      fetchNewsletterSubscribers();
    }
  };

  const handleOpenAnalyticsPage = () => {
    if (!onOpenAnalyticsPage) return;

    onOpenAnalyticsPage({
      postSlug,
      postPage,
      period,
      fromDate,
      toDate,
    });
  };


  const Trend = ({ value, inverse }) => {
    const isPositive = value >= 0;


    return (
      <span
        className={`text-xs font-semibold ${isPositive ? "text-green-600" : "text-red-600"
          }`}
      >
        {isPositive ? "▲" : "▼"} {Math.abs(value)}%
      </span>
    );
  };
  const Stat = ({ label, value, trend, inverse }) => (
    <div className="border rounded-lg p-3 hover:shadow-md transition-shadow">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
      {trend !== undefined && (
        <Trend value={trend} inverse={inverse} />
      )}
    </div>
  );


  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      onRequestClose={onClose}
      contentClassName="max-w-6xl w-full"
    >
      <div className="">
        <h5 className="mb-3 text-lg md:text-xl font-semibold">Post Analytics</h5>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
          <select
            value={period}
            onChange={(e) => {
              const newPeriod = e.target.value;
              setPeriod(newPeriod);
              setApplyCustom(0);
              
              if (newPeriod === "custom") {
                const today = new Date().toISOString().split('T')[0];
                const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                  .toISOString()
                  .split('T')[0];
                setFromDate(sevenDaysAgo);
                setToDate(today);
              }
            }}
            className="border rounded px-3 py-2 text-sm w-full sm:w-auto"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="6mo">Last 6 months</option>
            <option value="custom">Custom</option>
          </select>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleOpenAnalyticsPage}
              disabled={!postPage || !authorId}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto justify-center"
              title="Open full analytics page"
            >
              <BiLinkExternal className="w-4 h-4" />
              <span>Full Analytics</span>
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto justify-center"
              title="Refresh analytics data"
            >
              <svg
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {period === "custom" && (
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end mb-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date().toISOString().split('T')[0];
                  
                  if (toDate && selectedDate > toDate) {
                    return;
                  }
                  if (selectedDate > today) {
                    return;
                  }
                  
                  setFromDate(selectedDate);
                }}
                className="border rounded px-3 py-2 text-sm w-full sm:w-auto"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date().toISOString().split('T')[0];
                  
                  if (fromDate && selectedDate < fromDate) {
                    return;
                  }
                  if (selectedDate > today) {
                    return;
                  }
                  
                  setToDate(selectedDate);
                }}
                min={fromDate || undefined}
                className="border rounded px-3 py-2 text-sm w-full sm:w-auto"
              />
            </div>
            <button
              onClick={() => setApplyCustom(prev => prev + 1)}
              disabled={!fromDate || !toDate}
              className="bg-typeflo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              Apply
            </button>
          </div>
        )}

      <Tabs value={activeTab} onChange={setActiveTab}>
        <TabList>
          <TabNav value="pageviews">Page Views</TabNav>
          <TabNav value="clicks">CTA / Button</TabNav>
          <TabNav value="newsletter">Newsletter</TabNav>
        </TabList>

        {/* CLICKS TAB */}
        <TabContent value="clicks">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loading loading />
            </div>
          ) : clicksData.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-base">No clicks found for this period.</p>
              <p className="text-gray-400 text-sm mt-2">Try selecting a different date range.</p>
            </div>
          ) : (
            <div className="space-y-2 mt-3">
              {clicksData.map((click, i) => (
                <div
                  key={i}
                  className="p-3 md:p-4 border rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 hover:shadow-md transition-shadow"
                >
                  <div className="flex-1 w-full sm:w-auto">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <p className="font-semibold text-base">{click.name}</p>
                      {click.trend !== undefined && click.trend !== 0 && (
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded inline-flex items-center gap-1 ${
                            click.trend > 0
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {click.trend > 0 ? "▲" : "▼"} {Math.abs(click.trend)}%
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500">
                        Last clicked:{" "}
                        {click.lastClicked
                          ? new Date(click.lastClicked).toLocaleString()
                          : "—"}
                      </p>
                      <p className="text-xs text-gray-400">
                        Previous period: {click.prevCount || 0} clicks
                      </p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    <p className="text-3xl md:text-4xl font-bold text-typeflo-600">
                      {click.count}
                    </p>
                    <p className="text-xs text-gray-500">
                      {click.count === 1 ? "click" : "clicks"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabContent>

        {/* PAGE VIEWS TAB */}
        <TabContent value="pageviews">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loading loading />
            </div>
          ) : !pageViewData ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-base">No page view data available.</p>
              <p className="text-gray-400 text-sm mt-2">Data may take some time to sync.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              <Stat 
                label="Page Views" 
                value={pageViewData.pageviews} 
                trend={pageViewData.pageviewsTrend}
              />
              <Stat 
                label="Unique Visitors"
                value={pageViewData.visitors}
                trend={pageViewData.visitorsTrend}
              />
              <Stat 
                label="Bounce Rate" 
                value={`${pageViewData.bounce_rate}%`}
                trend={pageViewData.bounceRateTrend}
              />
              <Stat 
                label="Avg Visit Duration"
                value={`${pageViewData.visit_duration}s` || "0s"}
                trend={pageViewData.visitDurationTrend}
              />
              <Stat 
                label="CTA Clicks" 
                value={pageViewData.cta_clicks}
                trend={pageViewData.ctaClicksTrend}
              />
              <Stat 
                label="Button Clicks" 
                value={pageViewData.button_clicks}
                trend={pageViewData.buttonClicksTrend}
              />
              <Stat 
                label="CTR" 
                value={`${pageViewData.ctr}%`}
                trend={pageViewData.ctrTrend}
              />
              <Stat 
                label="Button CTR" 
                value={`${pageViewData.button_ctr}%`}
                trend={pageViewData.buttonCtrTrend}
              />
            </div>
          )}
          {!loading && pageViewData && ctrMeta && (
            <div className="mt-6 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
              <span className="font-semibold">Note:</span> CTR calculated from{" "}
              {new Date(ctrMeta).toLocaleDateString()} (when CTA tracking started)
            </div>
          )}
        </TabContent>

        {/* NEWSLETTER TAB */}
        <TabContent value="newsletter">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loading loading />
            </div>
          ) : !newsletterData?.subscribers || newsletterData.subscribers.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-base">No newsletter subscribers found for this period.</p>
              <p className="text-gray-400 text-sm mt-2">Try selecting a different date range.</p>
            </div>
          ) : (
            <div className="space-y-4 mt-3">
              {/* Summary Stats Card */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-gray-50 rounded-lg border">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-gray-500 mb-1">New Subscribers</p>
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <p className="text-2xl font-bold text-typeflo-600">{newsletterData.stats.currentCount}</p>
                    {newsletterData.stats.subscriptionRate !== 0 && (
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded inline-flex items-center gap-1 ${
                          newsletterData.stats.subscriptionRate > 0
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {newsletterData.stats.subscriptionRate > 0 ? "▲" : "▼"} {Math.abs(newsletterData.stats.subscriptionRate)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xs text-gray-500 mb-1">Previous Period</p>
                  <p className="text-2xl font-bold text-gray-600">{newsletterData.stats.previousCount}</p>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xs text-gray-500 mb-1">Growth Rate</p>
                  <p className={`text-2xl font-bold ${
                    newsletterData.stats.subscriptionRate > 0 
                      ? "text-green-600" 
                      : newsletterData.stats.subscriptionRate < 0 
                        ? "text-red-600" 
                        : "text-gray-600"
                  }`}>
                    {newsletterData.stats.subscriptionRate > 0 ? "+" : ""}{newsletterData.stats.subscriptionRate}%
                  </p>
                </div>
              </div>

              {/* Subscribers List */}
              <div className="space-y-2">
                <h6 className="text-sm font-semibold text-gray-700">Subscriber List</h6>
                {newsletterData.subscribers.map((subscriber, i) => (
                  <div
                    key={i}
                    className="p-3 md:p-4 border rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 hover:shadow-md transition-shadow"
                  >
                    <div className="flex-1 w-full sm:w-auto">
                      <p className="font-semibold text-base">{subscriber.email}</p>
                      {subscriber.name && (
                        <p className="text-sm text-gray-600 mt-1">{subscriber.name}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Subscribed: {new Date(subscriber.subscribedAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-left sm:text-right w-full sm:w-auto">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        ✓ Subscribed
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabContent>

      </Tabs>
      </div>
    </Dialog>
  );
};

export default PostAnalyticsDialog;
